/**
 * Filter, sort and pagination state for the Users Management list.
 * Extracted from UsersManagement to keep that file under 500 lines.
 */

import { useState, useEffect, useMemo } from 'react';
import type { UserProfile } from '../../lib/api';
import { departmentLabel } from '../../lib/departments';
import type { SortField, SortDirection } from './UserTable';
import {
  compareUsers, isAwaitingInvite, matchesDepartment, matchesTab,
} from './usersFilters';
import type {
  ApprovalFilter, DepartmentFilter, StatusFilter, UserTypeTab,
} from './usersFilters';

const ITEMS_PER_PAGE = 20;

interface UseUsersListArgs {
  /** Every user the list endpoint returned for the active view scope. */
  users: UserProfile[];
  /** The Customer / Supplier / Valuechain tab currently shown. */
  userTypeTab: UserTypeTab;
  /** Internal Helix viewer — the only kind that may see Valuechain users. */
  isHelixViewer: boolean;
}

export interface UseUsersListReturn {
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  statusFilter: StatusFilter;
  setStatusFilter: (v: StatusFilter) => void;
  approvalFilter: ApprovalFilter;
  setApprovalFilter: (v: ApprovalFilter) => void;
  companyFilter: string;
  setCompanyFilter: (v: string) => void;
  roleFilter: string;
  setRoleFilter: (v: string) => void;
  departmentFilter: DepartmentFilter;
  setDepartmentFilter: (v: DepartmentFilter) => void;
  hasActiveFilters: boolean;
  clearFilters: () => void;
  sortField: SortField;
  sortDirection: SortDirection;
  handleSort: (field: SortField) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalPages: number;
  /** Every row matching tab + filters, sorted — what the CSV export receives. */
  filteredAndSortedUsers: UserProfile[];
  /** The current page of {@link filteredAndSortedUsers}. */
  paginatedUsers: UserProfile[];
  uniqueCompanies: string[];
  uniqueRoles: string[];
  customerCount: number;
  supplierCount: number;
  valuechainCount: number;
  joinedCount: number;
  notJoinedCount: number;
  invitedSentCount: number;
  notInvitedCount: number;
  uniqueCompanyCount: number;
  joinedPercent: number;
}

/**
 * Client-side filter, sort and paging over the fetched user list.
 *
 * The list endpoint is walked to the end on load, so every filter here works
 * over the full scoped set and the page is just a slice of the result.
 * Changing any filter returns to page 1; a page left past the end (a row
 * edited out of the filter, a refetch with fewer rows) snaps back to the last
 * page instead of rendering an empty one.
 *
 * @param args - Users, active tab and whether the viewer is internal Helix
 * @returns Filter state and setters, sort state, paging, and derived counts
 *
 * @example
 * const list = useUsersList({ users, userTypeTab, isHelixViewer });
 * <UserTable users={list.paginatedUsers} totalPages={list.totalPages} ... />
 */
export function useUsersList({ users, userTypeTab, isHelixViewer }: UseUsersListArgs): UseUsersListReturn {
  const [searchTerm, setSearchTerm] = useState('');
  // CR-5312: default to Active so admins triaging the list don't see
  // deactivated users by default. The "All" / "Inactive" options stay
  // available for cleanup workflows.
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [approvalFilter, setApprovalFilter] = useState<ApprovalFilter>('all');
  const [companyFilter, setCompanyFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [departmentFilter, setDepartmentFilter] = useState<DepartmentFilter>('all');

  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => { setCurrentPage(1); },
    [searchTerm, statusFilter, approvalFilter, companyFilter, roleFilter, departmentFilter]);

  // CR-5367: Customer tab strictly excludes Helix users (company_type='helix').
  const customerCount = useMemo(
    () => users.filter(u => u.company_type !== 'supplier' && u.company_type !== 'helix').length,
    [users]
  );
  const supplierCount = useMemo(() => users.filter(u => u.company_type === 'supplier').length, [users]);
  const valuechainCount = useMemo(() => users.filter(u => u.company_type === 'helix').length, [users]);
  const uniqueCompanies = useMemo(() => Array.from(new Set(users.map(u => u.company_name))).sort(), [users]);
  const uniqueRoles = useMemo(() => Array.from(new Set(users.map(u => u.role_name).filter(Boolean))).sort(), [users]);

  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users.filter(u => matchesTab(u, userTypeTab, isHelixViewer));
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(u =>
        u.user.full_name.toLowerCase().includes(search) ||
        u.user.email.toLowerCase().includes(search) ||
        u.company_name.toLowerCase().includes(search) ||
        u.job_title?.toLowerCase().includes(search) ||
        // Match the label the column shows as well as the stored value, so
        // searching "Procurement" finds a "purchasing" row.
        departmentLabel(u.department).toLowerCase().includes(search) ||
        u.department?.toLowerCase().includes(search)
      );
    }
    if (statusFilter === 'invited') {
      filtered = filtered.filter(isAwaitingInvite);
    } else if (statusFilter === 'active') {
      // "Active" means the badge that reads Active — an invited-but-not-joined
      // row is still is_active, so filtering on that flag alone would include
      // rows the column labels Invite Sent.
      filtered = filtered.filter(u => u.user.is_active && !isAwaitingInvite(u));
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(u => !u.user.is_active);
    }
    if (approvalFilter !== 'all') filtered = filtered.filter(u => u.is_approved === (approvalFilter === 'approved'));
    if (companyFilter !== 'all') filtered = filtered.filter(u => u.company_name === companyFilter);
    if (roleFilter !== 'all') filtered = filtered.filter(u => u.role_name === roleFilter);
    if (departmentFilter !== 'all') filtered = filtered.filter(u => matchesDepartment(u, departmentFilter));

    filtered.sort(compareUsers(sortField, sortDirection));
    return filtered;
  }, [users, userTypeTab, isHelixViewer, searchTerm, statusFilter, approvalFilter,
    companyFilter, roleFilter, departmentFilter, sortField, sortDirection]);

  const totalPages = Math.ceil(filteredAndSortedUsers.length / ITEMS_PER_PAGE);
  // A row edited out of the active filter can shrink the result below the
  // stored page; show the last real page rather than an empty table.
  const page = Math.min(currentPage, Math.max(totalPages, 1));
  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredAndSortedUsers.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredAndSortedUsers, page]);

  const hasActiveFilters = Boolean(searchTerm) || statusFilter !== 'all' || approvalFilter !== 'all' ||
    companyFilter !== 'all' || roleFilter !== 'all' || departmentFilter !== 'all';

  const clearFilters = () => {
    setSearchTerm(''); setStatusFilter('all'); setApprovalFilter('all');
    setCompanyFilter('all'); setRoleFilter('all'); setDepartmentFilter('all');
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };

  // Stats over the filtered set.
  const joinedCount = useMemo(() => filteredAndSortedUsers.filter(u => u.user.has_joined).length, [filteredAndSortedUsers]);
  const notJoinedCount = filteredAndSortedUsers.length - joinedCount;
  const invitedSentCount = useMemo(() =>
    filteredAndSortedUsers.filter(u => !u.user.has_joined && u.user.invitation_sent).length,
    [filteredAndSortedUsers]);
  const notInvitedCount = useMemo(() =>
    filteredAndSortedUsers.filter(u => !u.user.has_joined && !u.user.invitation_sent).length,
    [filteredAndSortedUsers]);
  const uniqueCompanyCount = useMemo(() => new Set(filteredAndSortedUsers.map(u => u.company_name)).size, [filteredAndSortedUsers]);
  const joinedPercent = filteredAndSortedUsers.length > 0
    ? Math.round((joinedCount / filteredAndSortedUsers.length) * 100) : 0;

  return {
    searchTerm, setSearchTerm, statusFilter, setStatusFilter,
    approvalFilter, setApprovalFilter, companyFilter, setCompanyFilter,
    roleFilter, setRoleFilter, departmentFilter, setDepartmentFilter,
    hasActiveFilters, clearFilters,
    sortField, sortDirection, handleSort,
    currentPage: page, setCurrentPage, totalPages,
    filteredAndSortedUsers, paginatedUsers,
    uniqueCompanies, uniqueRoles,
    customerCount, supplierCount, valuechainCount,
    joinedCount, notJoinedCount, invitedSentCount, notInvitedCount,
    uniqueCompanyCount, joinedPercent,
  };
}
